import { FreezerRoom } from '../scenes/FreezerRoom';
import { Freezer } from '../scenes/Freezer';
import { SampleData } from '../scenes/SampleBox';
import { SAMPLE_TYPE_LABELS } from '../utils/materials';

export interface SearchResult {
  freezerId: string;
  shelfId: string;
  boxId: string;
  sample: SampleData;
}

export class SearchPanel {
  private room: FreezerRoom;
  private onResultsCallback: ((results: SearchResult[]) => void) | null = null;

  constructor(room: FreezerRoom) {
    this.room = room;
    this.populateFreezerFilter();
    this.bindEvents();
  }

  private populateFreezerFilter(): void {
    const select = document.getElementById('filter-freezer') as HTMLSelectElement;
    for (const freezer of this.room.freezers) {
      const opt = document.createElement('option');
      opt.value = freezer.freezerData.id;
      opt.textContent = `${freezer.freezerData.label} (${freezer.freezerData.temperature}°C)`;
      select.appendChild(opt);
    }
  }

  private bindEvents(): void {
    document.getElementById('search-btn')!.addEventListener('click', () => this.doSearch());
    document.getElementById('reset-btn')!.addEventListener('click', () => this.resetSearch());
    document.getElementById('detail-close')!.addEventListener('click', () => this.hideDetail());

    // Enter key on inputs
    const inputs = document.querySelectorAll('#search-panel input');
    inputs.forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') this.doSearch();
      });
    });
  }

  onResults(callback: (results: SearchResult[]) => void): void {
    this.onResultsCallback = callback;
  }

  private doSearch(): void {
    const typeFilter = (document.getElementById('filter-type') as HTMLSelectElement).value;
    const donorFilter = (document.getElementById('filter-donor') as HTMLInputElement).value.trim().toUpperCase();
    const dateFilter = (document.getElementById('filter-date') as HTMLInputElement).value;
    const freezerFilter = (document.getElementById('filter-freezer') as HTMLSelectElement).value;

    const results: SearchResult[] = [];
    const matchingFreezerIds = new Set<string>();

    for (const freezer of this.room.freezers) {
      if (freezerFilter && freezer.freezerData.id !== freezerFilter) continue;

      for (const shelf of freezer.shelves) {
        for (const box of shelf.boxes) {
          for (const [, mesh] of box.sampleMeshes) {
            const sample = mesh.userData.sampleData as SampleData;
            if (typeFilter && sample.type !== typeFilter) continue;
            if (donorFilter && !sample.donor.toUpperCase().includes(donorFilter)) continue;
            if (dateFilter && sample.date < dateFilter) continue;

            results.push({
              freezerId: freezer.freezerData.id,
              shelfId: shelf.shelfData.id,
              boxId: box.boxData.id,
              sample,
            });
            matchingFreezerIds.add(freezer.freezerData.id);
          }
        }
      }
    }

    // Dim non-matching freezers
    this.room.dimAllExcept([...matchingFreezerIds]);

    // Highlight matching samples
    this.clearHighlights();
    for (const result of results) {
      const freezer = this.room.getFreezerById(result.freezerId);
      if (!freezer) continue;
      for (const shelf of freezer.shelves) {
        for (const box of shelf.boxes) {
          if (box.boxData.id === result.boxId) {
            box.highlightSample(result.sample.id);
          }
        }
      }
    }

    this.onResultsCallback?.(results);
  }

  private clearHighlights(): void {
    for (const freezer of this.room.freezers) {
      for (const shelf of freezer.shelves) {
        for (const box of shelf.boxes) {
          box.unhighlightAll();
        }
      }
    }
  }

  resetSearch(): void {
    (document.getElementById('filter-type') as HTMLSelectElement).value = '';
    (document.getElementById('filter-donor') as HTMLInputElement).value = '';
    (document.getElementById('filter-date') as HTMLInputElement).value = '';
    (document.getElementById('filter-freezer') as HTMLSelectElement).value = '';

    this.room.resetDimming();
    this.clearHighlights();
    this.hideDetail();
    this.onResultsCallback?.([]);
  }

  showDetail(sample: SampleData, freezer: Freezer): void {
    const panel = document.getElementById('detail-panel')!;
    const title = document.getElementById('detail-title')!;
    const content = document.getElementById('detail-content')!;

    title.textContent = `Minta: ${sample.id}`;

    const typeLabel = SAMPLE_TYPE_LABELS[sample.type] || sample.type;
    const rows = [
      ['Típus', typeLabel],
      ['Donor', sample.donor],
      ['Dátum', sample.date],
      ['Mennyiség', `${sample.volume} ${sample.unit}`],
      ['Pozíció', `[${sample.position[0]}, ${sample.position[1]}]`],
      ['Hűtő', freezer.freezerData.label],
      ['Hőmérséklet', `${freezer.freezerData.temperature}°C`],
      ['Helyszín', freezer.freezerData.location],
    ];

    content.innerHTML = rows
      .map(
        ([label, value]) =>
          `<div class="detail-row"><span class="label">${label}</span><span class="value">${value}</span></div>`
      )
      .join('');

    panel.style.display = 'block';
  }

  hideDetail(): void {
    document.getElementById('detail-panel')!.style.display = 'none';
  }
}
